import React, { useEffect, useMemo, useState } from "react";
import { authFetch, isAdmin, isOperator } from "../../auth";
import { ActionModal } from "../Common/ActionModal";
import { usePermission } from "../Auth/PermissionContext";
import { PermissionKeys } from "../../permissions";

function employeeTypeName(value) {
  if (value == null) return "-";
  if (value === 0) return "管理员";
  if (value === 1) return "调度员";
  if (value === 2) return "监控员";
  return String(value);
}

const employeeTypeOptions = [
  { value: "0", label: "管理员" },
  { value: "1", label: "调度员" },
  { value: "2", label: "监控员" }
];

export function EmployeeManagement() {
  const admin = isAdmin();
  const operator = isOperator();
  const { can } = usePermission();
  const canView = can(PermissionKeys.EmployeeView);
  const canCreate = can(PermissionKeys.EmployeeCreate);
  const canUpdate = can(PermissionKeys.EmployeeUpdate);
  const canDelete = can(PermissionKeys.EmployeeDelete);

  const [name, setName] = useState("");
  const [employeeType, setEmployeeType] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [customerError, setCustomerError] = useState("");

  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [editModal, setEditModal] = useState({ open: false, row: null, name: "", employeeType: "" });
  const [deleteModal, setDeleteModal] = useState({ open: false, row: null });
  const [modalSubmitting, setModalSubmitting] = useState(false);

  const pageCount = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total]);
  const customerOptions = useMemo(() => ([
    { value: "", label: "全部客户" },
    ...customers.map((customer) => ({
      value: String(customer.id),
      label: `${customer.name}${customer.code ? ` / ${customer.code}` : ""} #${customer.id}`
    }))
  ]), [customers]);

  useEffect(() => {
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (!admin || operator || !canView) return;
    loadCustomerOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin, operator, canView]);

  async function loadCustomerOptions() {
    try {
      const res = await authFetch("/api/customers/options");
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "客户列表加载失败");
      }
      const data = await res.json();
      setCustomers(Array.isArray(data?.items) ? data.items : []);
      setCustomerError("");
    } catch (err) {
      setCustomers([]);
      setCustomerError(err?.message || "客户列表加载失败");
    }
  }

  async function loadEmployees(resetMsg = false) {
    setLoading(true);
    setError("");
    if (resetMsg) setMessage("");

    try {
      const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (name.trim()) qs.set("name", name.trim());
      if (employeeType !== "") qs.set("employeeType", employeeType);
      if (admin && customerId.trim()) qs.set("customerId", customerId.trim());

      const res = await authFetch(`/api/employees?${qs.toString()}`);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "员工查询失败");
      }
      const data = await res.json();
      setItems(data?.items || []);
      setTotal(Number(data?.total || 0));
    } catch (err) {
      setError(err?.message || "员工查询失败");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  function onSearch(e) {
    e.preventDefault();
    setPage(1);
    setTimeout(() => loadEmployees(true), 0);
  }

  async function onCreateFromFilters() {
    if (operator) return;

    setError("");
    setMessage("");

    const nextName = name.trim();
    const nextEmployeeType = employeeType === "" ? null : Number(employeeType);
    const nextCustomerId = admin && customerId.trim() ? Number(customerId.trim()) : null;

    if (!nextName) {
      setError("请先在查询条件中填写员工姓名。");
      return;
    }
    if (nextEmployeeType == null) {
      setError("请先在查询条件中选择员工类型。");
      return;
    }
    if (admin && !nextCustomerId) {
      setError("请先在查询条件中选择客户。");
      return;
    }

    const payload = {
      name: nextName,
      employeeType: nextEmployeeType,
      customerId: nextCustomerId
    };

    const res = await authFetch("/api/employees", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
      const txt = await res.text();
      setError(txt || "创建员工失败");
      return;
    }

    setMessage(`员工 ${payload.name} 创建成功。`);
    await loadEmployees();
  }

  function openEditModal(row) {
    if (operator || !canUpdate) return;
    setEditModal({
      open: true,
      row,
      name: row.name || "",
      employeeType: row.employeeType == null ? "" : String(row.employeeType)
    });
  }

  async function submitEdit(form) {
    const row = editModal.row;
    if (!row) return;

    const nextName = (form.name || "").trim();
    if (!nextName) {
      setError("员工姓名不能为空。");
      return;
    }

    const nextTypeRaw = (form.employeeType || "").trim();
    if (nextTypeRaw === "") {
      setError("员工类型不能为空。");
      return;
    }

    setModalSubmitting(true);
    setError("");
    setMessage("");

    try {
      const res = await authFetch(`/api/employees/${row.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: nextName,
          employeeType: nextTypeRaw === "" ? null : Number(nextTypeRaw)
        })
      });

      if (!res.ok) {
        const txt = await res.text();
        setError(txt || "更新员工失败");
        return;
      }

      setEditModal({ open: false, row: null, name: "", employeeType: "" });
      setMessage(`员工 ${row.name} 已更新。`);
      await loadEmployees();
    } finally {
      setModalSubmitting(false);
    }
  }

  function openDeleteModal(row) {
    if (operator || !canDelete) return;
    setDeleteModal({ open: true, row });
  }

  async function submitDelete() {
    const row = deleteModal.row;
    if (!row) return;

    setModalSubmitting(true);
    setError("");
    setMessage("");

    try {
      const res = await authFetch(`/api/employees/${row.id}`, { method: "DELETE" });
      if (!res.ok) {
        const txt = await res.text();
        setError(txt || "删除员工失败");
        return;
      }

      setDeleteModal({ open: false, row: null });
      setMessage(`员工 ${row.name} 已删除。`);
      await loadEmployees();
    } finally {
      setModalSubmitting(false);
    }
  }

  if (operator || !canView) {
    return (
      <div className="container-fluid">
        <div className="status-banner warn mt-3">当前账号无员工管理权限。</div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card shadow-sm status-card mt-3">
        <div className="card-header bg-primary text-white">员工管理</div>
        <div className="card-body">
          <form className="row g-2 align-items-end" onSubmit={onSearch}>
            <div className="col-md-3">
              <label className="form-label">员工姓名</label>
              <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label">员工类型</label>
              <select className="form-select" value={employeeType} onChange={(e) => setEmployeeType(e.target.value)}>
                <option value="">全部</option>
                {employeeTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            {admin && (
              <div className="col-md-3">
                <label className="form-label">客户</label>
                <select className="form-select" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
                  {customerOptions.map((option) => (
                    <option key={option.value || "all"} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="col-md-2">
              <button className="btn btn-primary w-100" type="submit" disabled={loading}>
                {loading ? "查询中..." : "查询"}
              </button>
            </div>
            {canCreate && (
              <div className="col-md-1">
                <button className="btn btn-success w-100" type="button" onClick={onCreateFromFilters} disabled={loading}>
                  添加
                </button>
              </div>
            )}
          </form>

          {error && <div className="status-banner error mt-3">{error}</div>}
          {customerError && <div className="status-banner warn mt-3">客户列表加载失败：{customerError}</div>}
          {message && <div className="status-banner info mt-3">{message}</div>}

          <div className="table-responsive mt-3">
            <table className="table table-sm table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>姓名</th>
                  <th>类型</th>
                  <th>客户</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {!loading && items.length === 0 && (
                  <tr><td colSpan={5}><div className="status-banner empty m-0">暂无员工数据</div></td></tr>
                )}
                {items.map((row) => (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>{row.name}</td>
                    <td>{employeeTypeName(row.employeeType)}</td>
                    <td>{row.customerName || `#${row.customerId}`}</td>
                    <td>
                      <div className="d-flex gap-1 flex-wrap">
                        {canUpdate && (
                          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => openEditModal(row)}>编辑</button>
                        )}
                        {canDelete && (
                          <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => openDeleteModal(row)}>删除</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-2">
            <div className="text-muted" style={{ fontSize: 13 }}>共 {total} 条，当前第 {page}/{pageCount} 页</div>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary btn-sm" type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>上一页</button>
              <button className="btn btn-outline-secondary btn-sm" type="button" disabled={page >= pageCount || loading} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>下一页</button>
            </div>
          </div>
        </div>
      </div>

      <ActionModal
        open={editModal.open && canUpdate}
        title="编辑员工"
        message={editModal.row ? `员工：${editModal.row.name}` : ""}
        fields={[
          { name: "name", label: "员工姓名", required: true },
          { name: "employeeType", label: "员工类型", type: "select", options: employeeTypeOptions, required: true }
        ]}
        initialValues={{ name: editModal.name, employeeType: editModal.employeeType }}
        submitting={modalSubmitting}
        onCancel={() => setEditModal({ open: false, row: null, name: "", employeeType: "" })}
        onConfirm={submitEdit}
      />

      <ActionModal
        open={deleteModal.open && canDelete}
        title="删除员工"
        message={deleteModal.row ? `确认删除员工：${deleteModal.row.name} 吗？` : ""}
        fields={[]}
        initialValues={{}}
        confirmText="确认删除"
        confirmClassName="btn-danger"
        submitting={modalSubmitting}
        onCancel={() => setDeleteModal({ open: false, row: null })}
        onConfirm={submitDelete}
      />
    </div>
  );
}
